// =============================================
// utils.js - Utility Functions for Exam Platform
//
// This file contains utility functions used across the application,
// including the guest to registered user migration functionality.
//
// Last updated: 2025-07-04 00:45:00 UTC
// =============================================

import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "./firebase";

/**
 * Migrates guest exam attempts to a registered user account.
 * This function is called when a guest user registers or logs in.
 * 
 * @param {string} newUserId - The new registered user's UID
 * @param {string} guestId - The guest ID from localStorage
 * @returns {Promise<{success: boolean, migratedCount: number, error?: string}>}
 */
export const migrateGuestAttempts = async (newUserId, guestId) => {
  console.log('[migrateGuestAttempts] Starting migration for guest:', guestId, 'to user:', newUserId);
  
  try {
    // Find all attempts with the guest ID
    const attemptsQuery = query(
      collection(db, "attempts"),
      where("userId", "==", guestId)
    );
    
    const attemptsSnapshot = await getDocs(attemptsQuery);
    const attempts = attemptsSnapshot.docs;
    
    console.log('[migrateGuestAttempts] Found', attempts.length, 'attempts to migrate');
    
    if (attempts.length === 0) {
      console.log('[migrateGuestAttempts] No attempts found to migrate');
      return { success: true, migratedCount: 0 };
    }
    
    // Update each attempt to use the new user ID
    const updatePromises = attempts.map(async (attemptDoc) => {
      const attemptData = attemptDoc.data();
      console.log('[migrateGuestAttempts] Migrating attempt:', attemptDoc.id, 'testName:', attemptData.testName);
      
      await updateDoc(doc(db, "attempts", attemptDoc.id), {
        userId: newUserId,
        migratedFromGuest: true,
        originalGuestId: guestId,
        migratedAt: new Date()
      });
    });
    
    await Promise.all(updatePromises);
    
    console.log('[migrateGuestAttempts] Successfully migrated', attempts.length, 'attempts');
    
    // Clear guest session data from localStorage
    localStorage.removeItem('guestId');
    localStorage.removeItem('isGuest');
    localStorage.removeItem('guestUserRegistered');
    localStorage.removeItem('guestUserId');
    
    return { success: true, migratedCount: attempts.length };
    
  } catch (error) {
    console.error('[migrateGuestAttempts] Error during migration:', error);
    return { 
      success: false, 
      migratedCount: 0, 
      error: error.message 
    };
  }
};

/**
 * Checks if a user has any guest attempts that need to be migrated.
 * This is used to determine if migration should be triggered.
 * 
 * @param {string} guestId - The guest ID to check
 * @returns {Promise<boolean>}
 */
export const hasGuestAttempts = async (guestId) => {
  try {
    const attemptsQuery = query(
      collection(db, "attempts"),
      where("userId", "==", guestId)
    );
    
    const attemptsSnapshot = await getDocs(attemptsQuery);
    return !attemptsSnapshot.empty;
  } catch (error) {
    console.error('[hasGuestAttempts] Error checking for guest attempts:', error);
    return false;
  }
};

/**
 * Migrates guest exam attempts from localStorage to Firebase for a registered user.
 * This function is called when a guest user registers or logs in.
 * 
 * @param {string} newUserId - The new registered user's UID
 * @returns {Promise<{success: boolean, migratedCount: number, error?: string}>}
 */
export const migrateLocalStorageGuestAttempts = async (newUserId) => {
  console.log('[migrateLocalStorageGuestAttempts] Starting migration for user:', newUserId);
  
  try {
    // Get guest attempts from localStorage
    const guestAttemptsJson = localStorage.getItem('guestAttempts');
    if (!guestAttemptsJson) {
      console.log('[migrateLocalStorageGuestAttempts] No guest attempts found in localStorage');
      return { success: true, migratedCount: 0 };
    }
    
    const guestAttempts = JSON.parse(guestAttemptsJson);
    if (!Array.isArray(guestAttempts) || guestAttempts.length === 0) {
      console.log('[migrateLocalStorageGuestAttempts] No valid guest attempts to migrate');
      return { success: true, migratedCount: 0 };
    }
    
    console.log('[migrateLocalStorageGuestAttempts] Found', guestAttempts.length, 'attempts to migrate');
    
    // Import Firebase functions for adding documents
    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
    
    // Create Firebase documents for each guest attempt
    const migrationPromises = guestAttempts.map(async (guestAttempt) => {
      console.log('[migrateLocalStorageGuestAttempts] Migrating attempt:', guestAttempt.id, 'testName:', guestAttempt.testName);
      
      // Prepare the data for Firebase
      const attemptData = {
        userId: newUserId,
        answers: guestAttempt.answers || {},
        score: guestAttempt.score || 0,
        correctAnswers: guestAttempt.correctAnswers || 0,
        totalQuestions: guestAttempt.totalQuestions || 0,
        incorrectAnswers: guestAttempt.incorrectAnswers || 0,
        performance: guestAttempt.performance || [],
        completed: true,
        testName: guestAttempt.testName || 'Guest Exam',
        difficulty: guestAttempt.difficulty || 'Intermediate',
        timeTaken: guestAttempt.timeTaken || 0,
        createdAt: serverTimestamp(),
        migratedFromGuest: true,
        originalGuestId: guestAttempt.userId,
        migratedAt: serverTimestamp()
      };
      
      // Add custom exam specific fields if present
      if (guestAttempt.isCustom) {
        attemptData.isCustom = true;
        attemptData.questionIds = guestAttempt.questionIds || [];
      } else {
        attemptData.questionSetId = guestAttempt.questionSetId;
      }
      
      // Create the document in Firebase
      await addDoc(collection(db, "attempts"), attemptData);
    });
    
    await Promise.all(migrationPromises);
    
    console.log('[migrateLocalStorageGuestAttempts] Successfully migrated', guestAttempts.length, 'attempts');
    
    // Clear guest session data from localStorage
    localStorage.removeItem('guestAttempts');
    localStorage.removeItem('guestId');
    localStorage.removeItem('isGuest');
    localStorage.removeItem('guestUserRegistered');
    localStorage.removeItem('guestUserId');
    
    return { success: true, migratedCount: guestAttempts.length };
    
  } catch (error) {
    console.error('[migrateLocalStorageGuestAttempts] Error during migration:', error);
    return { 
      success: false, 
      migratedCount: 0, 
      error: error.message 
    };
  }
};

/**
 * Checks if there are guest attempts in localStorage that need to be migrated.
 * 
 * @returns {boolean}
 */
export const hasLocalStorageGuestAttempts = () => {
  try {
    const guestAttemptsJson = localStorage.getItem('guestAttempts');
    if (!guestAttemptsJson) return false;
    
    const guestAttempts = JSON.parse(guestAttemptsJson);
    return Array.isArray(guestAttempts) && guestAttempts.length > 0;
  } catch (error) {
    console.error('[hasLocalStorageGuestAttempts] Error checking for guest attempts:', error);
    return false;
  }
}; 